import { Settings, reducedVfx } from "../ui/settings";
import { isRareEnemy } from "../rare/rareApi";
import { isWyrm } from "../rare/wyrmApi";
import { WYRM_CUE } from "../audio/wyrmCues";
import { audio } from "../audio/audio";
import * as THREE from "three";
import { groundAt } from "./terrain";
import { LevelSim, SimConstants, SimEventKind, isBeam } from "@highmarch/domain";
import { muzzleFallback, surfaceY } from "./views";
import { VfxRouter, enemyThisTick } from "./vfxRouter";
import { RELEASE_FPS, TOWER_SHOT, releaseSeconds } from "./views/projectiles";
import { towerFireRate } from "./views/towers";
import { LIGHTNING_RELEASE_FRAME, impactGate, roundOf, shotKey, statusGate } from "./statusFx";
import { TowerFeel } from "./views/towerFeel";
import type { AudioCue } from "../audio/audio";
import { deathCue, directHits, placeCue, upgradeCue, waveCue } from "../audio/cueRules";
import { BuildCueBeats } from "../audio/buildCues";
import { ParticlePool } from "./effectsParticles";

/** Each tower family has its own shot (the v6 packs, docs/AUDIO.md). */
const FIRE_CUES: Record<string, AudioCue> = { archer: "fire_archer", ballista: "fire_ballista", cannon: "fire_cannon", frost: "fire_frost", fire: "fire_fire", poison: "fire_poison", lightning: "fire_lightning", beacon: "fire_beacon", mortar: "fire_mortar" };

/**
 * Seconds from TowerFired to the round leaving the muzzle (the clip's release frame, at the rate the clip plays), so
 * the shot is heard as it is seen.
 */
function releaseDelay(towerId: string, rate: number): number {
  const shot = TOWER_SHOT[towerId];
  if (towerId === "lightning") return LIGHTNING_RELEASE_FRAME / RELEASE_FPS / Math.max(1, rate);   // the arcs leave on the clip's flash
  return shot ? releaseSeconds(shot, rate) : 0;
}

/**
 * Lightweight, pooled effects: additive point particles (hits, deaths, portal sparkle, upgrades) and simple
 * projectiles (arrows, cannonballs) driven from sim events. Presentation only; nothing here affects the sim.
 */
export class EffectsView {
  readonly group = new THREE.Group();
  /** The built-in particles and rounds (effectsParticles.ts), drawn when the sprite VFX has not loaded. */
  private readonly fx = new ParticlePool(this.group);

  /** Whether a fortress shot is its charged strike rather than a zap (TowerViews.heroShot); unset: every shot is. */
  baseShotIsHero: ((towerId: number) => boolean) | null = null;

  constructor(private readonly sim: LevelSim) {}

  /**
   * Seconds from a placement to the start of its cue. `towerPlace` is cut to the placed tower's own clock (audio/
   * buildCues.ts): it starts on the tap with the drop and the `build` clip, a rush under the fall and the thud on
   * `BuildCueBeats.towerPlace.land`, which is where the drop ends (TowerFeel.DropSeconds), so the start is the gap
   * between the two — none while they agree, and the thud stays on the landing frame if either moves. The clip runs
   * in real time (views/towers.ts update), so the cue lands at ×1, ×2 and ×3 alike, and it plays under Reduced effects
   * too, where the tower does not drop and the thud meets the plinth rising. A road segment has no animation: it is
   * down on the next frame, and so is its clack.
   */
  static placeDelay(kind: SimEventKind): number {
    return kind === SimEventKind.TowerPlaced ? TowerFeel.DropSeconds - BuildCueBeats.towerPlace.land : 0;
  }

  /**
   * Seconds from a tier-up to the start of its cue: the stab sits on `BuildCueBeats.towerUpgrade.swap`, where the
   * ascension swaps the model under its flash (TowerFeel.AscendSquash after the tap).
   */
  static upgradeDelay(): number {
    return TowerFeel.AscendSquash - BuildCueBeats.towerUpgrade.swap;
  }

  /**
   * Where this tick's unseen events start. A command's events are appended to the buffer of the tick they land in,
   * and the controller hands the whole buffer over again after each command (levelController.execute), so without this
   * every tap during a battle replayed the last tick's shots, hits and deaths (sounds and sprites) around its own cue.
   */
  private firstUnseen(): number {
    const tick = this.sim.tick, count = this.sim.events.count, seen = this.seen;
    const from = seen.tick === tick && seen.count <= count ? seen.count : 0;
    seen.tick = tick; seen.count = count;
    return from;
  }
  private readonly seen = { tick: -1, count: 0 };

  /** Sound cues for this tick's events from `from` on (rate-limited inside the audio port). */
  private playAudio(from: number): void {
    const events = this.sim.events.all();
    const direct = directHits(events);                     // burn ticks are not hits
    // a tower's hits and kills are heard when its round lands (statusFx.ts ImpactGate), not as it leaves; with no
    // rounds drawn (no sprite VFX) there is nothing to wait for
    const rounds = this.router ? roundOf(events) : null;
    const onLanding = (i: number, fn: () => void): void => {
      const tower = rounds?.[i] ?? 0;
      if (tower > 0) impactGate.wait(shotKey(this.sim.tick, tower), events[i].a, fn); else fn();
    };
    let wave: "waveTick" | "waveMilestone" | null = null;
    for (let i = from; i < events.length; i++) {
      const ev = events[i];
      switch (ev.kind) {
        case SimEventKind.TowerFired: {
          const t = this.sim.findTower(ev.a); if (!t) break;
          const id = this.sim.balance.towers[t.archetype].id;
          // the fortress's strike (the shot its charge ends on) gets the heavy cue; the zaps between sound like the
          // lightning tower they look like, so the strike sound no longer lands on random zaps
          if (this.sim.balance.towers[t.archetype].isBase) audio.play(this.baseShotIsHero?.(t.id) ?? true ? "baseStrike" : "fire_lightning");
          else if (isBeam(this.sim.balance.towers[t.archetype])) break;     // a held beam's pulse: the hum carries it
          else audio.play(FIRE_CUES[id] ?? "fire_archer", releaseDelay(id, towerFireRate(this.sim, t)));
          break;
        }
        // TOWERS_V2 (art/audio/v6/towers_v2.py): the shell's burst, a Barracks' gate as its soldiers muster, a spire
        // locking on, a curse landing (the shell's whistle and the spire's hum are render/towersV2Fx.ts's)
        case SimEventKind.ShellLanded: audio.play("mortarImpact"); break;
        case SimEventKind.TroopSpawned: if ((this.sim.troops.find(ev.a)?.ownerTowerId ?? 0) !== 0) audio.play("barracksDeploy"); break;
        case SimEventKind.BeamStarted: audio.play("fire_arcane"); break;
        case SimEventKind.EnemyHexed: audio.play("hexCurse"); break;
        case SimEventKind.EnemyDamaged: if (direct[i]) onLanding(i, () => audio.play("hit")); break;
        case SimEventKind.EnemyKilled: {
          // findEnemy only returns the living, and the kill is reported after the body is marked dead
          // M40: the Starfall Wyrm's fall starts with its own crack and cry (its crash: render/views/wyrmView.ts)
          const dead = enemyThisTick(this.sim, ev.a);
          const cue = dead && isWyrm(this.sim, dead) ? WYRM_CUE.kill : deathCue(dead, (a) => this.sim.balance.enemies[a].id);
          onLanding(i, () => audio.play(cue));
          break;
        }
        // M38: the war call opens the battle once; it no longer rings on every stage start and surge (20-40 times a
        // level, "becoming annoying"). A wave's end gets a soft tick, or a short brass call on a milestone (waveCue).
        case SimEventKind.WavesStarted: audio.play("waveStart"); break;
        case SimEventKind.StageCleared: {
          const sim = this.sim;
          const cue = waveCue(ev.a, { stages: sim.stages, checkpointEvery: sim.balance.economy.checkpointEveryStages, campaign: !sim.isEndless && !sim.isIron }, this.lastMilestone);
          if (cue === "waveMilestone") this.lastMilestone = ev.a;
          if (cue !== null && wave !== "waveMilestone") wave = cue;      // overlapping stages clear together: one cue, the bigger
          break;
        }
        case SimEventKind.EnemySpawned: {
          // A spawn after a quiet spell is a new surge or raid (DESIGN §3.2): it gets a portal pulse (seen, not heard).
          const t = this.sim.tick;
          if (t - this.lastSpawnTick >= EffectsView.SurgeQuietTicks && t - this.sim.stageStartTick >= EffectsView.SurgeQuietTicks) this.surge = true;
          this.lastSpawnTick = t;
          break;
        }
        case SimEventKind.EnemyLeaked: audio.play("leak"); break;
        case SimEventKind.TowerPlaced: case SimEventKind.SegmentPlaced: audio.play(placeCue(ev.kind), EffectsView.placeDelay(ev.kind)); break;
        case SimEventKind.TowerMoved: audio.play(placeCue(SimEventKind.TowerPlaced), EffectsView.placeDelay(SimEventKind.TowerPlaced)); break;
        case SimEventKind.TowerUpgraded: {
          const t = this.sim.findTower(ev.a);                // b is the tier just reached
          audio.play(upgradeCue(ev.b, t ? this.sim.balance.towers[t.archetype].tiers.length : ev.b), EffectsView.upgradeDelay());
          break;
        }
        case SimEventKind.BossSpawned: if (!isRareEnemy(this.sim, ev.a)) audio.play("boss"); break;   // M36: the rare has its own stinger
        // The chest cue belongs to the chest opening on the end card (ui/chestSlot.ts), not to its roll under the stinger.
        // The battle bed fades under the win/lose stinger; the menu's hub bed takes over from there.
        case SimEventKind.LevelWon: audio.play("win"); audio.music(null); break;
        case SimEventKind.LevelLost: audio.play("lose"); audio.music(null); break;
        default: break;
      }
    }
    if (wave !== null && Settings.current.waveChimes) audio.play(wave);
  }

  /** Reacts to this tick's sim events. Reduced-effects setting (or OS reduced motion) skips the particle work. */
  /** The art session's sprite VFX once its atlas has loaded (art/vfx.js); events it handles skip the built-in particles. */
  router: VfxRouter | null = null;
  dispose(scene: THREE.Scene): void {
    this.router?.dispose(scene); this.router = null;
    // Made per level, so freed with it: undisposed, the renderer's attribute bindings kept them (and their GPU
    // buffers) for good (RC1 FX-C).
    this.fx.dispose();
  }

  /** Ticks without a spawn after which the next spawn counts as a new surge. */
  static readonly SurgeQuietTicks = 3 * SimConstants.TicksPerSecond;
  private lastSpawnTick = -Infinity;
  private surge = false;
  /** The last wave whose end got the milestone call (cueRules.waveCue), 0 for none yet. */
  private lastMilestone = 0;

  onTick(): void {
    this.surge = false;
    const from = this.firstUnseen();
    this.playAudio(from);
    if (reducedVfx()) { this.shotsOnly(from); return; }
    if (this.surge) this.router?.surge();
    const sim = this.sim;
    this.router?.beginTick();
    const events = sim.events.all();
    for (let i = from; i < events.length; i++) {
      const ev = events[i];
      if (this.router?.onEvent(ev)) continue;
      switch (ev.kind) {
        case SimEventKind.TowerFired: {
          const t = sim.findTower(ev.a); const e = sim.findEnemy(ev.b);
          if (!t || !e) break;
          const id = sim.balance.towers[t.archetype].id;
          if (id === "mortar" || id === "arcane") break;                   // drawn by render/towersV2Fx.ts
          const to = new THREE.Vector3(e.x.toNumber(), groundAt(e.x.toNumber(), e.y.toNumber()) + 0.2, e.y.toNumber());
          const from = muzzleFallback(id, t.tier, t.cell, Math.atan2(to.x - t.cell.x, to.z - t.cell.y));
          if (id === "cannon" || id === "fire") this.fx.shoot(from, to, 0.35, 0.9, this.fx.ballGeo, this.fx.ballMat, () => this.fx.burst(to, 18, 1.0, 0.55, 0.2, 0.09, 2.2));
          else if (id === "frost") this.fx.burst(to, 6, 0.6, 0.9, 1.0, 0.05, 0.6);
          else if (id === "lightning") this.fx.spark(from, to);
          else this.fx.shoot(from, to, 0.12, 0, this.fx.arrowGeo, this.fx.arrowMat, () => this.fx.burst(to, 5, 1.0, 0.9, 0.6, 0.04, 1.2));
          break;
        }
        case SimEventKind.EnemyKilled: {
          const e = sim.enemySlots.find((x) => x.id === ev.a);
          if (e) { const p = new THREE.Vector3(e.x.toNumber(), groundAt(e.x.toNumber(), e.y.toNumber()) + 0.4, e.y.toNumber()); this.fx.burst(p, 26, 1.0, 0.35, 0.25, 0.1, 2.4); this.fx.burst(p, 10, 1.0, 0.85, 0.3, 0.07, 1.6); }
          break;
        }
        case SimEventKind.EnemyLeaked: {
          const b = sim.grid.base;
          this.fx.burst(new THREE.Vector3(b.x, groundAt(b.x, b.y) + 0.6, b.y), 30, 1.0, 0.2, 0.2, 0.12, 2.6);
          break;
        }
        case SimEventKind.TowerUpgraded: {
          const t = sim.findTower(ev.a);
          if (t) this.fx.burst(new THREE.Vector3(t.cell.x, groundAt(t.cell.x, t.cell.y) + 0.6, t.cell.y), 30, 1.0, 0.9, 0.4, 0.09, 1.4, 0.4);
          break;
        }
        case SimEventKind.TowerPlaced: case SimEventKind.TowerMoved:
          this.fx.burst(new THREE.Vector3(ev.b, groundAt(ev.b, ev.c) + 0.2, ev.c), 20, 0.6, 0.9, 0.5, 0.08, 1.2, 0.3);
          break;
        case SimEventKind.EnemySpawned: {
          const p = sim.road.portal;
          this.fx.burst(new THREE.Vector3(p.x, groundAt(p.x, p.y) + 0.4, p.y), 8, 0.75, 0.4, 1.0, 0.07, 1.4, 0.2);
          break;
        }
        case SimEventKind.BossSpawned: {
          // Boss intro (P1.8.2): a red-gold eruption at the portal plus a ring of sparks racing outward along the ground.
          const p = sim.road.portal;
          const at = new THREE.Vector3(p.x, groundAt(p.x, p.y) + 0.3, p.y);
          this.fx.burst(at, 40, 1.0, 0.35, 0.2, 0.12, 2.6, 0.9);
          this.fx.burst(at, 24, 1.0, 0.85, 0.3, 0.09, 1.6, 1.1);
          for (let i = 0; i < 28; i++) {
            const a = (i / 28) * Math.PI * 2;
            this.fx.emit(at.x, surfaceY() + 0.08, at.z, Math.cos(a) * 3.2, 0.2, Math.sin(a) * 3.2, 0.1, 1.0, 0.45, 0.25, 0.7, 1.5);
          }
          break;
        }
      }
    }
    this.router?.endTick();
  }

  /**
   * Reduced effects: the towers' rounds still fly (they are what says who is shooting what), without the bursts, the
   * surge and the other extras. It used to route nothing at all, so no round flew (TOWER_FX follow-up). The sprite
   * rounds drop their trails and ribbons themselves under reduced effects (views/projectiles.ts); a fortress strike is
   * left out — its beats are all extras — and its hits show at once (the router settles an unclaimed shot).
   */
  private shotsOnly(from: number): void {
    const sim = this.sim, events = sim.events.all(), router = this.router;
    router?.beginTick();
    for (let i = from; i < events.length; i++) {
      const ev = events[i];
      if (ev.kind !== SimEventKind.TowerFired) continue;
      const t = sim.findTower(ev.a);
      if (!t || sim.balance.towers[t.archetype].isBase) continue;
      if (router) { router.onEvent(ev); continue; }
      const e = sim.findEnemy(ev.b);
      if (!e) continue;
      const id = sim.balance.towers[t.archetype].id;
      if (id === "mortar" || id === "arcane") continue;                 // drawn by render/towersV2Fx.ts
      const to = new THREE.Vector3(e.x.toNumber(), groundAt(e.x.toNumber(), e.y.toNumber()) + 0.2, e.y.toNumber());
      const at = muzzleFallback(id, t.tier, t.cell, Math.atan2(to.x - t.cell.x, to.z - t.cell.y));
      if (id === "cannon" || id === "fire") this.fx.shoot(at, to, 0.35, 0.9, this.fx.ballGeo, this.fx.ballMat);
      else if (id !== "frost" && id !== "lightning") this.fx.shoot(at, to, 0.12, 0, this.fx.arrowGeo, this.fx.arrowMat);
    }
    router?.endTick();
  }

  update(dt: number): void {
    this.router?.update(dt);
    statusGate.advance(dt);                              // the status gate's timeout runs on the rounds' clock
    if (Math.random() < dt * 12) {
      const p = this.sim.road.portal;
      const a = Math.random() * Math.PI * 2;
      this.fx.emit(p.x + Math.cos(a) * 0.4, surfaceY() + 0.55, p.y + Math.sin(a) * 0.4, 0, 0.6, 0, 0.05, 0.8, 0.5, 1.0, 0.8, 0);
    }
    this.fx.step(dt);
  }
}
